import {
  MainContentWrapper,
  SiteHeaderContent,
  ViewWrapper,
} from "@/components/layout/ViewWrapper.tsx";
import { Loader } from "@/components/loader/Loader.tsx";
import { SavedObjectView, savedObjectServiceName } from "@/components/canvas/SavedObjectView.tsx";
import { useSourceRoute } from "@/hooks/useSourceRoute.ts";
import { useEffect } from "react";
import { useLocation, useRoute } from "wouter";

/**
 * Backstop resolver for bare source urns: `/sources/:id` looks up the source's
 * user-facing type and redirects to the proper detail page. Type-keyed links
 * (e.g. `/communications/:id`) skip this hop entirely.
 */
export const SourceRedirect = () => {
  const [, params] = useRoute("/sources/:id");
  const id = params?.id;
  const [, setLocation] = useLocation();
  const { route, isLoading, error, data } = useSourceRoute({ id });

  useEffect(() => {
    if (route) setLocation(route, { replace: true });
  }, [route, setLocation]);

  if (isLoading || route) {
    return (
      <ViewWrapper headerContent={<SiteHeaderContent includeBackButton title="" />}>
        <MainContentWrapper>
          <Loader className="py-8" />
        </MainContentWrapper>
      </ViewWrapper>
    );
  }

  // A saved connector object — meeting notes, a page — has no page of its own
  // to redirect to, so it is read here.
  if (data?.type === "connectorEntity") {
    return (
      <ViewWrapper
        headerContent={
          <SiteHeaderContent
            includeBackButton
            title={savedObjectServiceName(data.detail.service)}
          />
        }
      >
        <MainContentWrapper>
          <SavedObjectView detail={data.detail} />
        </MainContentWrapper>
      </ViewWrapper>
    );
  }

  // An error (e.g. a 404 from a stale/invalid link) means the source is gone;
  // a resolved-but-null route means the source exists but has no detail page yet.
  const title = error ? "Not found" : "Source";
  const message = error ? "Source not found." : "This item can't be viewed yet.";

  return (
    <ViewWrapper headerContent={<SiteHeaderContent includeBackButton title={title} />}>
      <MainContentWrapper>
        <p className="text-muted-foreground text-sm p-4">{message}</p>
      </MainContentWrapper>
    </ViewWrapper>
  );
};
